/**
 * Evaluates a single ConditionalDisplay or CompoundConditionalDisplay against answers.
 */
import { ConditionalDisplay, CompoundConditionalDisplay } from '@/components/assessment/conversational/questionTypes';

export function isCompoundCondition(
  cd: ConditionalDisplay | CompoundConditionalDisplay
): cd is CompoundConditionalDisplay {
  return 'type' in cd && ('conditions' in cd);
}

/**
 * Check a single simple condition against current answers.
 * Supports operators: equals (default), not_equals, in, not_includes, contains.
 */
export function checkSimpleCondition(cond: ConditionalDisplay, answers: Record<string, any>): boolean {
  const answer = answers[cond.dependsOn];
  if (answer === undefined || answer === null) return false;

  const op = cond.operator || 'equals';

  if (op === 'contains') {
    // For multi-select: check if the array answer contains any of the showWhen values
    if (Array.isArray(answer)) {
      return cond.showWhen.some(val => answer.includes(val));
    }
    return false;
  }

  // Delegate to existing matcher for other operators
  // Re-use the __not: prefix convention for not_equals/not_includes
  if (op === 'not_equals' || op === 'not_includes') {
    const notPrefixed = cond.showWhen.map(v => `__not:${v}`);
    return checkConditionMatch(notPrefixed, Array.isArray(answer) ? answer : answer);
  }

  // equals / in
  const answerToCheck = typeof answer === 'object' && answer !== null && 'value' in answer ? answer.value : answer;
  return checkConditionMatch(cond.showWhen, answerToCheck);
}

/**
 * Evaluate a possibly-compound conditional display rule.
 */
export function evaluateConditionalDisplay(
  cd: ConditionalDisplay | CompoundConditionalDisplay,
  answers: Record<string, any>
): boolean {
  if (isCompoundCondition(cd)) {
    if (cd.type === 'OR') {
      return cd.conditions.some(c => checkSimpleCondition(c, answers));
    }
    // AND
    return cd.conditions.every(c => checkSimpleCondition(c, answers));
  }
  return checkSimpleCondition(cd, answers);
}

/**
 * Evaluates conditional display rules against an answer value.
 * Supports string answers, array (multi-select) answers, and __not: prefix.
 */
export function checkConditionMatch(showWhen: string[], answer: any): boolean {
  if (answer === undefined || answer === null) return false;

  const notConditions = showWhen.filter(c => c.startsWith('__not:'));
  const positiveConditions = showWhen.filter(c => !c.startsWith('__not:'));

  if (Array.isArray(answer)) {
    for (const nc of notConditions) {
      const excludeVal = nc.slice(6);
      if (answer.includes(excludeVal)) return false;
    }
    if (notConditions.length > 0 && positiveConditions.length === 0) return true;
    return positiveConditions.some(c => answer.includes(c));
  }

  // String answer
  return showWhen.some(condition => {
    if (condition.startsWith('__not:')) {
      return answer !== condition.slice(6);
    }
    return condition === answer;
  });
}
